import React, {useEffect, useMemo, useState} from 'react';
import {observer} from 'mobx-react-lite';
import {ViewMode} from './CalendarBoxState';
import {FrappeGantt} from './gantt/gantt';
import {IntlDAteFormat} from '../../../helpers/formats';
import {appState} from 'store/AppStore';
import {LeftOutlined, RightOutlined} from '@ant-design/icons';
import {Button, Table} from 'antd';
import {useTranslation} from 'react-i18next';
import {ReservationsState} from 'store/reservations/ReservationsState';
import {useSearchParams} from 'react-router-dom';
import {SorterResult} from 'antd/es/table/interface';
import {ReservationDto} from '../../../../../../PRMGApiClient';
import {ganttReservationsColumns} from '../GanttFilterColumns';
import {browserHistory} from 'routing/browserHistory';
import {AppRoutePaths} from 'routing/Routes';

export const CalendarBox: React.FC = observer(() => {
  const state = appState.calendarBoxState;
  const [reservationsState] = useState(new ReservationsState());
  const [searchParams, setSearchParams] = useSearchParams();
  const data = useMemo(() => reservationsState.items, [reservationsState.items]);
  const tasks = reservationsState.calendarTaskList;

  const {t} = useTranslation();

  useEffect(() => {
    reservationsState.loadTableItems();
  }, [reservationsState]);

  const columns = useMemo(() => {
    for (const column of ganttReservationsColumns) {
      if (column.key === 'car') {
        column.filters = appState.carBrandModelsTree;
      }
      if (column.key === 'customer') {
        column.filters = [];
        for (const {customer} of data) {
          if (customer) {
            if (!column.filters?.find((filter) => filter.value === customer.customerId)) {
              column.filters.push({
                text: `${customer.firstName} ${customer.lastName}`,
                value: customer.customerId ?? '',
              });
            }
          }
        }
      }
    }

    return ganttReservationsColumns;
  }, [data]);

  useEffect(() => {
    const vehicleId = searchParams.get('vehicleId');
    if (vehicleId) reservationsState.setFiltersVehicle(Number(vehicleId));
  }, [searchParams, setSearchParams, reservationsState]);

  return (
    <div className="mt-5 h-88%">
      <div className="w-full h-full items-center justify-end">
        <div className="w-auto flex items-center justify-between">
          <div className="w-auto flex items-center">
            <Button onClick={() => state?.loadNextDate(false)} className="p-0 border-none shadow-none">
              <LeftOutlined />
              {IntlDAteFormat(new Date(state.begins))} /
            </Button>
            <Button onClick={() => state?.loadNextDate(true)} className="ml-1 p-0 border-none shadow-none">
              {IntlDAteFormat(new Date(state.ends))}
              <RightOutlined />
            </Button>
          </div>
          <div className="w-auto flex items-center justify-end">
            <Button
              type={state.viewMode === ViewMode.Day ? 'primary' : 'default'}
              className={'mr-4 btn-empty-underline w-[6.25rem]'}
              onClick={() => state.changeViewMode(ViewMode.Day)}
            >
              {t('Month')}
            </Button>
            <Button
              type={state.viewMode === ViewMode.Month ? 'primary' : 'default'}
              className={'btn-empty-underline pr-2 w-[6.25rem]'}
              onClick={() => state.changeViewMode(ViewMode.Month)}
            >
              {t('Year')}
            </Button>
          </div>
        </div>
        <div className="px-14">
          <Table
            rowKey="id"
            className="gantt-filters mt-8"
            columns={columns}
            loading={appState.isLoading}
            onChange={(pagination, filters, sorters) => {
              reservationsState.setPagination(pagination);
              reservationsState.setFilters(filters);
              reservationsState.setSorters(sorters as SorterResult<ReservationDto>);
            }}
          />
          <div className="gantt-box h-full relative">
            <div className="slider right-full rounded-l-lg" onClick={() => state.loadNextDate(false)}>
              <LeftOutlined className="text-lg" />
            </div>
            <div className="overflow-hidden">
              {tasks.length > 0 && (
                <FrappeGantt
                  key={state.begins + state.ends}
                  tasks={tasks}
                  viewMode={state.viewMode}
                  height={state.height}
                  setWidth={() => state.setWidth}
                  setHeight={() => state.setHeight}
                  onClick={(task) => browserHistory.push(`${AppRoutePaths.reservation.url}/${task.id}/edit`)}
                />
              )}
            </div>
            <div className="slider left-full rounded-r-lg" onClick={() => state.loadNextDate(true)}>
              <RightOutlined className="text-lg" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
});
